import React from 'react';
import { XMarkIcon, CalendarIcon, UserIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

interface NewsArticle {
  id: string;
  title: string;
  summary: string;
  body: string;
  author: string;
  date: string;
  formatted_date: string;
}

interface NewsDetailProps {
  article: NewsArticle | null;
  onClose: () => void;
}

const NewsDetail: React.FC<NewsDetailProps> = ({ article, onClose }) => {
  if (!article) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="min-h-screen px-4 text-center">
        {/* Centering trick */}
        <span className="inline-block h-screen align-middle" aria-hidden="true">
          &#8203;
        </span>
        
        <div className="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-2xl rounded-2xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={onClose}
              className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Back to News
            </button>
            
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Article Content */}
          <article className="prose prose-lg max-w-none">
            {/* Meta Information */}
            <div className="flex items-center text-sm text-gray-500 mb-6 not-prose">
              <CalendarIcon className="w-4 h-4 mr-2" />
              <time dateTime={article.date}>
                {article.formatted_date}
              </time>
              <span className="mx-3">•</span>
              <UserIcon className="w-4 h-4 mr-2" />
              <span>By {article.author}</span>
            </div>

            {/* Title */}
            <h1 className="text-3xl font-bold text-gray-900 mb-6 leading-tight">
              {article.title}
            </h1>

            {/* Summary */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-l-4 border-blue-500 p-6 mb-8 rounded-r-lg">
              <p className="text-lg text-gray-700 font-medium italic leading-relaxed">
                {article.summary}
              </p>
            </div>

            {/* Body */}
            <div className="text-gray-700 leading-relaxed space-y-6">
              {article.body.split('\n\n').map((paragraph, index) => (
                <p key={index} className="text-base leading-7">
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Additional Content Sections */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Key Takeaways</h2>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                  <span>Modern development practices are constantly evolving with new technologies and methodologies.</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                  <span>Staying updated with industry trends is crucial for maintaining competitive advantage.</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                  <span>Implementation of best practices ensures scalable and maintainable solutions.</span>
                </li>
              </ul>
            </div>

            {/* Tags */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Related Topics</h3>
              <div className="flex flex-wrap gap-2">
                {['Web Development', 'Technology', 'Innovation', 'Best Practices'].map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-gradient-to-r from-blue-100 to-purple-100 text-blue-800 text-sm font-medium rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {/* Footer Actions */}
          <div className="mt-12 pt-6 border-t border-gray-200 flex justify-between items-center">
            <div className="text-sm text-gray-500">
              Published on {article.formatted_date}
            </div>
            
            <div className="flex space-x-4">
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200">
                Share Article
              </button>
              <button 
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsDetail;